import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
// CDK drag-drop is no longer used by the canvas (we moved to free-form
// 2D positioning). Kept import-free here intentionally — if a future
// feature needs list reordering elsewhere, import locally.
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subscription } from "rxjs";
import { ActivatedRoute } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SessionService } from "src/app/services/session.service";
import { NavigationService } from "src/app/services/navigation.service";
import { WorkflowService } from "../../service/workflow.service";
import { TaskMgmtService } from "../../service/task-mgmt.service";
import {
  DesignJSON,
  DesignStep,
  StepDefinition,
  NewFieldSpec,
  STEP_DEFS,
  FIELDS,
  getStepDef as _getStepDef,
  WorkflowDefinition,
} from "./type-driven-dynamic-form";

@Component({
  selector: "app-workflow-designer",
  templateUrl: "./workflow-designer.component.html",
  styleUrls: ["./workflow-designer.component.scss"],
})
export class WorkflowDesignerComponent implements OnInit, OnDestroy, AfterViewInit {
  // Existing (kept)
  moduleName: string = "";
  clientKey: string = "";
  moduleKey: string = "";
  clientLogo: string = "";
  newColor: any;
  designerData: any;
  reqSubcription: Subscription[] = [];

  // New state
  currentWf: WorkflowDefinition | null = null;
  design: DesignJSON | null = null;
  // Selected step pointer. When configBranch is null, configStepIdx is the
  // top-level step index. When configBranch is 'yes'|'no', configStepIdx is
  // the parent condition's top-level index and configBranchIdx is the index
  // within that branch.
  configStepIdx: number | null = null;
  configBranch: 'yes' | 'no' | null = null;
  configBranchIdx: number | null = null;
  // True while the currently-open config panel is for a step that was just
  // added via the palette and hasn't been Applied yet. Used to auto-remove
  // the step if the user closes/discards without making a change.
  private isNewStep: boolean = false;
  private hasBeenApplied: boolean = false;
  dirty: boolean = false;
  nextStepKey: number = 1;
  paletteSearch: string = "";
  activeAddMenu: number | null = null;
  // Add menu opened inside a condition's branch arm. afterIdx === -1 means
  // "before any branch step" (the very first slot in the arm).
  activeBranchAddMenu: { stepIdx: number; branch: 'yes' | 'no'; afterIdx: number } | null = null;
  configForm: FormGroup | null = null;
  tagValues: { [key: string]: string[] } = {};
  // Stable, per-step caches. Populated in selectStep() and refreshed when
  // async lookups arrive. Bound directly in the template instead of method
  // calls — calling getFieldsForStep()/getOptsForField() from the template
  // returns a fresh array on every CD tick, which sends mat-select into a
  // change-detection loop and freezes the page.
  currentFields: NewFieldSpec[] = [];
  optionCache: { [key: string]: { id: any; name: string }[] } = {};
  lookups: { statuses: any[]; priorities: any[]; types: any[]; emailTemplates: any[]; eventTypes: any[] } = {
    statuses: [],
    priorities: [],
    types: [],
    emailTemplates: [],
    eventTypes: [],
  };
  // Brand context for the Email Alert template picker. Comes in as a query
  // param from the workflow-management page's brand dropdown; falls back to
  // a known seed brand when missing (legacy URLs / direct navigation).
  private brandKey: number | null = null;
  private readonly DEFAULT_TEMPLATE_BRAND_KEY = 200107;
  paletteOpen: boolean = true;
  // Snapshot of paletteOpen taken when a config panel opens. Restored
  // when the panel closes (Apply / Discard / Escape / ×) so the palette
  // returns to whatever the user had it set to before editing the step.
  private paletteStateBeforeConfig: boolean = true;

  // === ZOOM / PAN / MINIMAP ===
  // Refs into the canvas DOM. Resolved in ngAfterViewInit; redirected via
  // setters because the elements live inside a *ngIf so they can be (re)created.
  private _scrollEl: HTMLElement | null = null;
  private _shellEl: HTMLElement | null = null;
  private _canvasEl: HTMLElement | null = null;

  @ViewChild('canvasScroll') set canvasScrollRef(r: ElementRef<HTMLElement> | undefined) {
    this._scrollEl = r?.nativeElement || null;
    if (this._scrollEl) this.attachZoomInfra();
  }
  @ViewChild('zoomShell') set zoomShellRef(r: ElementRef<HTMLElement> | undefined) {
    this._shellEl = r?.nativeElement || null;
    if (this._shellEl) this.attachZoomInfra();
  }
  @ViewChild('canvasInner') set canvasInnerRef(r: ElementRef<HTMLElement> | undefined) {
    this._canvasEl = r?.nativeElement || null;
    if (this._canvasEl) this.attachZoomInfra();
  }
  // canvas-wrap is the element we request fullscreen on. It's outside the
  // *ngIf="design" gate so it's stable across loads — direct property ref.
  @ViewChild('canvasWrap') canvasWrapRef?: ElementRef<HTMLElement>;
  // The whole designer body (canvas + palette + config panel) is the
  // fullscreen target. Putting fullscreen on canvas-wrap alone hid the
  // palette / config panel because they're flex siblings outside the wrap.
  @ViewChild('bodyWrap') bodyWrapRef?: ElementRef<HTMLElement>;

  zoom: number = 1;
  zoomPercent: number = 100;
  private readonly ZOOM_MIN = 0.4;
  private readonly ZOOM_MAX = 1.5;
  private readonly ZOOM_STEP = 0.1;
  // Cached natural layout dimensions of .wf-canvas at zoom=1. Populated via
  // measure() and refreshed when content/viewport changes. We pin canvas
  // width to naturalW so transform: scale doesn't recursively change layout.
  private naturalW: number = 0;
  private naturalH: number = 0;
  private rafScheduled: boolean = false;
  private resizeObs: ResizeObserver | null = null;
  private viewportResizeObs: ResizeObserver | null = null;
  private zoomInfraReady: boolean = false;

  // Pan: middle-mouse drag or Alt+drag inside the scroll container.
  private isPanning: boolean = false;
  private panStartClientX: number = 0;
  private panStartClientY: number = 0;
  private panStartScrollX: number = 0;
  private panStartScrollY: number = 0;

  // Fullscreen state — true while the canvas wrap is the document's
  // fullscreen element. Synced via the document fullscreenchange event so
  // pressing Esc still updates the icon.
  isFullscreen: boolean = false;
  // ID is set when entering fullscreen so the SCSS can target the wrap with
  // a class hook (CSS :fullscreen pseudo only matches the topmost element).
  // We bind via [class.is-fullscreen] in the template.

  // === 2D CANVAS MODEL ===
  // Nodes drive the absolute-positioned render. Built from design.steps
  // (and nested branches) by buildCanvasModel(). Each entry references the
  // underlying DesignStep so edits route back through configForm/applyConfig
  // without changing the on-disk schema.
  canvasNodes: Array<{
    step: DesignStep;
    // Locator into the tree for select/remove operations:
    // { topIdx, branch?: 'yes'|'no', branchIdx? }
    topIdx: number;
    branch: 'yes' | 'no' | null;
    branchIdx: number | null;
  }> = [];
  // Derived edges. Recomputed alongside canvasNodes whenever the design
  // changes (add/remove/load). SVG paths re-render from this.
  canvasEdges: Array<{
    fromKey: string;
    toKey: string;
    kind: 'main' | 'yes' | 'no';
    path: string;
  }> = [];
  // Per-arm "+ Add" anchors for condition steps. Sit at the end of each
  // branch arm (or directly off the condition when the arm is empty) and
  // open the branch palette menu via toggleBranchAddMenu(). Without these
  // there's no way to populate a branch on the 2D canvas.
  canvasBranchAnchors: Array<{
    stepIdx: number;
    branch: 'yes' | 'no';
    afterIdx: number;
    x: number;
    y: number;
    fromKey: string;
    edgePath: string;
  }> = [];
  // Tail "+ Add" anchor for the top-level (non-branch) chain. Sits below
  // the last top-level step when that step is a non-terminator. Opens the
  // full step palette via toggleAddMenu(). Mirrors the YES/NO anchors so
  // every node in the chain has an in-canvas way to add the next step.
  canvasMainAnchor: {
    afterIdx: number;
    x: number;
    y: number;
    fromKey: string;
    edgePath: string;
  } | null = null;
  // Logical canvas size — bounding box of all nodes + breathing room. Drives
  // both the inner canvas dimensions and the minimap projection space.
  canvasW: number = 1800;
  canvasH: number = 1080;

  // Node geometry. Kept as constants because the SCSS pins these — changing
  // here without updating SCSS will desync edge endpoints from card edges.
  private readonly NODE_W = 220;
  private readonly NODE_H = 104;
  // Auto-layout grid spacing (also used for the canvas dot-grid background).
  private readonly LAYOUT_GRID = 20;
  private readonly LAYOUT_X_CENTER = 720;
  private readonly LAYOUT_Y_START = 60;
  private readonly LAYOUT_Y_SPACING = 140;
  private readonly LAYOUT_BRANCH_X = 280;

  // Free-form drag state. We track which logical node is being dragged plus
  // the offset from cursor to node origin, so during mousemove we set
  // node.canvas_x = cursorXInCanvas - dragOffsetX.
  private dragNode: DesignStep | null = null;
  private dragOffsetX: number = 0;
  private dragOffsetY: number = 0;
  // Suppress the click that fires after a real drag. mousedown→drag→mouseup
  // would otherwise fire selectStep on the underlying card.
  private dragMoved: boolean = false;

  // Minimap. Rendered as a list of rects projected from real cards.
  minimapOpen: boolean = true;
  minimapNodes: Array<{ left: number; top: number; width: number; height: number; kind: 'card' | 'trigger' | 'condition' | 'end' }> = [];
  minimapViewport: { left: number; top: number; width: number; height: number } = { left: 0, top: 0, width: 0, height: 0 };
  private readonly MINIMAP_W = 168;
  private readonly MINIMAP_H = 108;
  // Cached uniform-scale projection for the minimap. Computed in
  // updateMinimap() and reused by updateMinimapViewportOnly() and
  // onMinimapClick() so the viewport rect and click-to-pan share the
  // same coordinate system as the projected node rects.
  private minimapScale: number = 1;
  private minimapOffX: number = 0;
  private minimapOffY: number = 0;

  currentUserEmail: string = '';
  isLoadingDesigner: boolean = false;
  // True when loadFull failed for the requested wf_def_id. We deliberately
  // leave currentWf/design null so saveFlow() can never fire and clobber the
  // real server-side DRAFT with a blank design built from query-string data.
  loadFailed: boolean = false;
  // Snapshot of wf_name as last persisted (after load or save). onNameBlur
  // compares against this — not against the global `dirty` flag, which is
  // also flipped by step edits and would otherwise fire updateWorkflowMeta
  // racing with saveFlow on unrelated changes.
  private lastSavedName: string = '';

  // Assignee searchable picker state — mirrors task-detail-content's pattern.
  // Single set of state shared by every 'assignees' field rendered in the
  // panel; only one dropdown is open at a time so this is sufficient.
  assigneeInput: string = '';
  assigneeSearchResults: any[] = [];
  isSearchingUsers: boolean = false;
  showAssigneeResults: boolean = false;
  assigneeDisplayMap: { [email: string]: string } = {};
  // Public so the template can disambiguate which 'assignees' field's
  // dropdown is currently open (in case multiple assignees fields exist).
  assigneeFieldKey: string | null = null;
  private assigneeUsersCache: any[] = [];
  private assigneeSearchTimer: any = null;
  private assigneeBlurTimer: any = null;

  // Patient-status multi-select picker state — same pattern as assignees but
  // populated from GetpatientStatusCodeFilter() instead of the user list.
  statusPickerInput: string = '';
  statusPickerResults: Array<{ code: string; label: string }> = [];
  isLoadingStatuses: boolean = false;
  showStatusResults: boolean = false;
  statusPickerFieldKey: string | null = null;
  statusDisplayMap: { [code: string]: string } = {};
  private statusPickerCache: Array<{ code: string; label: string }> = [];
  private statusPickerTimer: any = null;
  private statusPickerBlurTimer: any = null;
  // Lives only while the Email Alert config panel is open; tracks the
  // use_template toggle so we can swap required-validators between
  // subject/body and email_template_key as the user flips it.
  private configToggleSub: Subscription | null = null;

  workName: string = "Create Workflow";
  componentName: string = "Workflow List";

  constructor(
    private fb: FormBuilder,
    private wfService: WorkflowService,
    private activeRoute: ActivatedRoute,
    private navigationService: NavigationService,
    private sessionService: SessionService,
    private snackBar: MatSnackBar,
    private taskMgmtService: TaskMgmtService,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    this.clientLogo = this.sessionService.getClintLogo();
    this.newColor = this.sessionService.getColorCodes();

    const userInfo = JSON.parse(sessionStorage.getItem('userInformation') || '{}');
    this.currentUserEmail = userInfo.usr_email_id || '';

    const routeSub = this.activeRoute.params.subscribe((p: any) => {
      this.moduleName = p["module"] || "";
    });
    this.reqSubcription.push(routeSub);

    const querySub = this.activeRoute.queryParams.subscribe((params: any) => {
      const decryptedParams = this.navigationService.decryptData(params);
      this.designerData = JSON.parse(decryptedParams.designerData);
      this.clientKey = decryptedParams["cl_key"];
      this.moduleKey = decryptedParams["md_key"];

      // brand_key arrives as a string after URL decryption; coerce to number
      // and fall back to the seed brand when missing (legacy URLs).
      const rawBrand = decryptedParams["brand_key"];
      const parsedBrand = rawBrand != null && rawBrand !== '' ? Number(rawBrand) : NaN;
      this.brandKey = !isNaN(parsedBrand) ? parsedBrand : this.DEFAULT_TEMPLATE_BRAND_KEY;
      this.loadEmailTemplates(this.brandKey);

      if (this.designerData?.wf_def_id != null) {
        this.loadDesigner(this.designerData.wf_def_id);
      }
    });
    this.reqSubcription.push(querySub);

    const statusSub = this.wfService
      .getTaskStatuses()
      .subscribe((data: any) => {
        this.lookups.statuses = data || [];
        this.rebuildOptionCache();
      });
    const prioritySub = this.wfService
      .getTaskPriorities()
      .subscribe((data: any) => {
        this.lookups.priorities = data || [];
        this.rebuildOptionCache();
      });
    const typesSub = this.wfService
      .getTaskTypes()
      .subscribe((data: any) => {
        this.lookups.types = data || [];
        this.rebuildOptionCache();
      });
    const eventTypesSub = this.wfService
      .getEventTypes()
      .subscribe((data: any) => {
        this.lookups.eventTypes = data || [];
        this.rebuildOptionCache();
      });
    this.reqSubcription.push(statusSub, prioritySub, typesSub, eventTypesSub);
  }

  /**
   * Fetch email templates for the given brand and refresh the option cache so
   * any open Email Alert config reflects the new list. Cached per-brand at
   * the service layer, so re-entering the designer for the same brand reuses
   * the prior result without a network call.
   */
  private loadEmailTemplates(brandKey: number): void {
    const sub = this.wfService
      .getEmailTemplatesForBrand(brandKey)
      .subscribe((data: any) => {
        this.lookups.emailTemplates = data || [];
        this.rebuildOptionCache();
      });
    this.reqSubcription.push(sub);
  }

  ngAfterViewInit(): void {
    // ViewChild setters fire when the *ngIf gates open after the workflow
    // finishes loading; nothing to do here for the common case. Kept so the
    // lifecycle interface is honored and any future eager wiring lands here.
  }

  ngOnDestroy(): void {
    this.reqSubcription.forEach((s) => s.unsubscribe());
    this.resizeObs?.disconnect();
    this.resizeObs = null;
    this.viewportResizeObs?.disconnect();
    this.viewportResizeObs = null;
  }

  // === ZOOM / PAN / MINIMAP ===

  // Wire up ResizeObservers + scroll listener once all three refs are ready.
  // Re-entrant safe: bails if any ref is missing or already attached.
  private attachZoomInfra(): void {
    if (this.zoomInfraReady) return;
    if (!this._scrollEl || !this._shellEl || !this._canvasEl) return;
    this.zoomInfraReady = true;

    // Watch the inner canvas for content-driven height changes (steps added
    // or removed). naturalH grows with content; reapply zoom + minimap.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObs = new ResizeObserver(() => this.scheduleReapply());
      this.resizeObs.observe(this._canvasEl);
      this.viewportResizeObs = new ResizeObserver(() => this.scheduleReapply());
      this.viewportResizeObs.observe(this._scrollEl);
    }

    // First-time measure happens off the next animation frame so Angular's
    // initial render of all child step cards has fully laid out.
    this.scheduleReapply();
  }

  private scheduleReapply(): void {
    if (this.rafScheduled) return;
    this.rafScheduled = true;
    // Run outside Angular: this fires on every scroll/resize. We don't need
    // change detection on every measure pass; bindings update via setters.
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.rafScheduled = false;
        this.measureAndApply();
      });
    });
  }

  private measureAndApply(): void {
    const scroll = this._scrollEl;
    const shell = this._shellEl;
    const canvas = this._canvasEl;
    if (!scroll || !shell || !canvas) return;

    // The 2D canvas's natural dimensions are canvasW × canvasH (set
    // explicitly via [style.width.px] / [style.height.px] in the template,
    // driven by the bbox of all nodes). No measurement dance needed —
    // we own the dimensions authoritatively.
    this.naturalW = this.canvasW;
    this.naturalH = this.canvasH;

    canvas.style.transform = `scale(${this.zoom})`;

    // Size the zoom shell so the scroll container scrolls the visually
    // scaled canvas correctly. Floor at viewport size so a tiny canvas
    // at low zoom doesn't leave empty space below the scrollbars.
    const w = Math.max(this.naturalW * this.zoom, scroll.clientWidth);
    const h = Math.max(this.naturalH * this.zoom, scroll.clientHeight);
    shell.style.width = w + 'px';
    shell.style.height = h + 'px';

    this.zone.run(() => this.updateMinimap());
  }

  zoomIn(): void {
    this.setZoom(this.zoom + this.ZOOM_STEP);
  }
  zoomOut(): void {
    this.setZoom(this.zoom - this.ZOOM_STEP);
  }
  zoomReset(): void {
    if (this.zoom === 1) return;
    this.setZoom(1);
  }

  private setZoom(z: number): void {
    const next = Math.max(this.ZOOM_MIN, Math.min(this.ZOOM_MAX, Math.round(z * 100) / 100));
    if (next === this.zoom) return;
    this.zoom = next;
    this.zoomPercent = Math.round(next * 100);
    this.scheduleReapply();
  }

  // Ctrl/Cmd + wheel inside the canvas zooms instead of scrolls. We swallow
  // the event so the browser doesn't ALSO zoom the page.
  onCanvasWheel(event: WheelEvent): void {
    if (!(event.ctrlKey || event.metaKey)) return;
    event.preventDefault();
    if (event.deltaY < 0) this.zoomIn();
    else this.zoomOut();
  }

  onCanvasScroll(): void {
    // Update the minimap viewport rect on every scroll, throttled to one
    // update per frame so a fast scroll doesn't queue up redundant work.
    if (this.rafScheduled) return;
    this.rafScheduled = true;
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.rafScheduled = false;
        this.zone.run(() => this.updateMinimapViewportOnly());
      });
    });
  }

  // Pan: middle-mouse-button (button === 1) OR Alt+left-click. We grab the
  // scroll position at down and translate cursor delta into scrollLeft/Top
  // adjustments. Pointer capture isn't strictly needed because we listen on
  // document for move/up so the user can drag past the canvas edge.
  onCanvasMouseDown(event: MouseEvent): void {
    const isMiddle = event.button === 1;
    const isAltLeft = event.button === 0 && event.altKey;
    if (!isMiddle && !isAltLeft) return;
    const scroll = this._scrollEl;
    if (!scroll) return;
    event.preventDefault();
    this.isPanning = true;
    this.panStartClientX = event.clientX;
    this.panStartClientY = event.clientY;
    this.panStartScrollX = scroll.scrollLeft;
    this.panStartScrollY = scroll.scrollTop;
    document.body.style.cursor = 'grabbing';
  }

  @HostListener('document:mousemove', ['$event'])
  onDocMouseMove(event: MouseEvent): void {
    if (!this.isPanning) return;
    const scroll = this._scrollEl;
    if (!scroll) return;
    scroll.scrollLeft = this.panStartScrollX - (event.clientX - this.panStartClientX);
    scroll.scrollTop = this.panStartScrollY - (event.clientY - this.panStartClientY);
  }

  @HostListener('document:mouseup')
  onDocMouseUp(): void {
    if (!this.isPanning) return;
    this.isPanning = false;
    document.body.style.cursor = '';
  }

  // Minimap projection. Reads positions directly from canvasNodes — no
  // DOM queries needed because each node knows its own (x, y) and the
  // node geometry is fixed. This is also zoom-agnostic since canvas_x/y
  // are LAYOUT coordinates, independent of CSS transform.
  private updateMinimap(): void {
    if (!this.minimapOpen) return;
    if (!this.canvasW || !this.canvasH) {
      this.minimapNodes = [];
      return;
    }
    // Uniform "fit-to-frame" scale. Using separate sx/sy distorts every
    // node rectangle whenever canvas aspect differs from the minimap's
    // 168:108 — a 220×96 card becomes a noticeably different shape than
    // it is on the canvas. One scale factor for both axes preserves the
    // canvas's geometry, then centering offsets place the projected
    // content inside the minimap frame.
    const s = Math.min(this.MINIMAP_W / this.canvasW, this.MINIMAP_H / this.canvasH);
    const projectedW = this.canvasW * s;
    const projectedH = this.canvasH * s;
    const offX = (this.MINIMAP_W - projectedW) / 2;
    const offY = (this.MINIMAP_H - projectedH) / 2;
    this.minimapScale = s;
    this.minimapOffX = offX;
    this.minimapOffY = offY;

    const out: Array<{ left: number; top: number; width: number; height: number; kind: 'card' | 'trigger' | 'condition' | 'end' }> = [];

    // Classify by step type only (mirroring the canvas card class
    // selectors). Branch arm membership doesn't change a step's identity,
    // so a regular action looks the same on the minimap whether it lives
    // at the top level or inside a YES/NO arm.
    this.canvasNodes.forEach((n) => {
      let kind: 'card' | 'trigger' | 'condition' | 'end' = 'card';
      if (this.isTriggerStep(n.step)) kind = 'trigger';
      else if (this.isConditionStep(n.step)) kind = 'condition';
      else if (this.isEndStep(n.step)) kind = 'end';
      out.push({
        left: offX + (n.step.canvas_x || 0) * s,
        top: offY + (n.step.canvas_y || 0) * s,
        width: Math.max(2, this.NODE_W * s),
        height: Math.max(2, this.NODE_H * s),
        kind,
      });
    });

    this.minimapNodes = out;
    this.updateMinimapViewportOnly();
  }

  private updateMinimapViewportOnly(): void {
    const scroll = this._scrollEl;
    if (!scroll || !this.minimapOpen) return;
    if (!this.canvasW || !this.canvasH || !this.zoom) return;

    // Convert the visible region from screen pixels back into canvas-layout
    // coordinates (the same space minimap nodes are projected from). Using
    // shell.offsetWidth here would be wrong: at low zoom the shell is
    // padded out to the viewport width so the dot-grid stays full, but
    // that padding isn't real canvas — it'd inflate the viewport rect
    // beyond the actual content area and desync from the node rects.
    const visStartX = scroll.scrollLeft / this.zoom;
    const visEndX = (scroll.scrollLeft + scroll.clientWidth) / this.zoom;
    const visStartY = scroll.scrollTop / this.zoom;
    const visEndY = (scroll.scrollTop + scroll.clientHeight) / this.zoom;

    // Clamp to canvas bounds so the rect doesn't run into the padded
    // empty area when canvasW × zoom < clientWidth.
    const left = Math.max(0, visStartX);
    const right = Math.min(this.canvasW, visEndX);
    const top = Math.max(0, visStartY);
    const bottom = Math.min(this.canvasH, visEndY);

    // Reuse the uniform projection from updateMinimap() so the viewport
    // rect and the node rects share the same coordinate system.
    const s = this.minimapScale;
    this.minimapViewport = {
      left: this.minimapOffX + left * s,
      top: this.minimapOffY + top * s,
      width: Math.max(0, (right - left) * s),
      height: Math.max(0, (bottom - top) * s),
    };
  }

  // Click on minimap → pan the scroll container so the click point is
  // centered in the viewport (clamped to scroll bounds). The click is
  // converted through the same uniform projection used by the node
  // rects so click-to-pan lands the cursor's location at the viewport
  // center, even when the canvas/minimap aspect ratios differ.
  onMinimapClick(event: MouseEvent): void {
    const scroll = this._scrollEl;
    const shell = this._shellEl;
    if (!scroll || !shell || !this.minimapScale) return;
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const localX = event.clientX - rect.left;
    const localY = event.clientY - rect.top;
    // Subtract the centering offset and undo the uniform scale to get
    // the click position in canvas-layout coords. Clamp so a click in
    // the empty letterbox area still pans somewhere sensible.
    const canvasX = Math.max(0, Math.min(this.canvasW,
      (localX - this.minimapOffX) / this.minimapScale));
    const canvasY = Math.max(0, Math.min(this.canvasH,
      (localY - this.minimapOffY) / this.minimapScale));
    // Project back into screen-pixel scroll coords (canvas × zoom).
    const targetScrollX = canvasX * this.zoom - scroll.clientWidth / 2;
    const targetScrollY = canvasY * this.zoom - scroll.clientHeight / 2;
    scroll.scrollLeft = Math.max(0, Math.min(targetScrollX, shell.offsetWidth - scroll.clientWidth));
    scroll.scrollTop = Math.max(0, Math.min(targetScrollY, shell.offsetHeight - scroll.clientHeight));
  }

  toggleMinimap(): void {
    this.minimapOpen = !this.minimapOpen;
    if (this.minimapOpen) this.scheduleReapply();
  }

  // === 2D CANVAS MODEL — auto-layout, edge derivation, drag ===
  //
  // The canvas is a flat list of absolutely-positioned nodes built from the
  // existing design.steps tree. Edges are derived from sequential order +
  // condition branches; we don't mutate the tree shape, only the (x, y) on
  // each step. This keeps the server contract intact — the render is the
  // only thing that changed.

  // Build the flat list of canvas nodes + derived edges. Called whenever
  // the structure changes (load, add, remove). Mutates step positions only
  // for steps that have no positions yet (auto-layout fallback).
  rebuildCanvasModel(): void {
    if (!this.design?.steps) {
      this.canvasNodes = [];
      this.canvasEdges = [];
      this.canvasBranchAnchors = [];
      this.canvasMainAnchor = null;
      this.minimapNodes = [];
      return;
    }
    this.autoLayoutIfNeeded();
    this.canvasNodes = this.flattenStepsForCanvas();
    this.canvasEdges = this.deriveEdges();
    this.canvasBranchAnchors = this.deriveBranchAnchors();
    this.canvasMainAnchor = this.deriveMainAnchor();
    this.recomputeCanvasBounds();
    // Refresh the minimap directly. measureAndApply() also calls
    // updateMinimap() but only fires when the canvas DOM size physically
    // changes; structural edits (add/remove/load) don't always change
    // canvasW/H, so without this call the minimap can hold stale rects.
    this.updateMinimap();
  }

  // Walk every step in the design (top-level + branches) and assign default
  // canvas_x / canvas_y when missing. Existing positions are preserved so
  // user-arranged layouts are respected on reload.
  private autoLayoutIfNeeded(): void {
    if (!this.design?.steps) return;
    let nextY = this.LAYOUT_Y_START;

    const layout = (step: DesignStep, x: number) => {
      if (!step.canvas_x || step.canvas_x === 0) step.canvas_x = x;
      if (!step.canvas_y || step.canvas_y === 0) step.canvas_y = nextY;
      nextY = Math.max(nextY, step.canvas_y) + this.LAYOUT_Y_SPACING;
    };

    this.design.steps.forEach((step) => {
      layout(step, this.LAYOUT_X_CENTER);
      if (step.yes_branch?.length) {
        const branchTopY = nextY;
        let yesY = branchTopY;
        step.yes_branch.forEach((s) => {
          if (!s.canvas_x || s.canvas_x === 0) s.canvas_x = this.LAYOUT_X_CENTER + this.LAYOUT_BRANCH_X;
          if (!s.canvas_y || s.canvas_y === 0) s.canvas_y = yesY;
          yesY = Math.max(yesY, s.canvas_y) + this.LAYOUT_Y_SPACING;
        });
        let noY = branchTopY;
        (step.no_branch || []).forEach((s) => {
          if (!s.canvas_x || s.canvas_x === 0) s.canvas_x = this.LAYOUT_X_CENTER - this.LAYOUT_BRANCH_X;
          if (!s.canvas_y || s.canvas_y === 0) s.canvas_y = noY;
          noY = Math.max(noY, s.canvas_y) + this.LAYOUT_Y_SPACING;
        });
        nextY = Math.max(yesY, noY);
      }
    });
  }

  // Scroll the canvas so the first node is horizontally centered in the
  // viewport on initial load. Uses rAF so the DOM has painted and clientWidth
  // reflects the actual available width before we calculate the offset.
  // Only called once after loadDesigner — never on subsequent rebuildCanvasModel
  // calls so the user's scroll position is not reset while editing.
  private scrollToFirstNode(): void {
    const scrollEl = this._scrollEl;
    if (!scrollEl || !this.design?.steps?.length) return;
    const first = this.design.steps[0];
    const x = first.canvas_x ?? this.LAYOUT_X_CENTER;
    const y = first.canvas_y ?? this.LAYOUT_Y_START;
    requestAnimationFrame(() => {
      scrollEl.scrollLeft = Math.max(0, x - scrollEl.clientWidth / 2 + this.NODE_W / 2);
      scrollEl.scrollTop  = Math.max(0, y - 80);
    });
  }

  // Flatten the design tree into a renderable list with locator info so
  // click/drag handlers can route back into the right array.
  private flattenStepsForCanvas(): typeof this.canvasNodes {
    const out: typeof this.canvasNodes = [];
    if (!this.design?.steps) return out;
    this.design.steps.forEach((step, topIdx) => {
      out.push({ step, topIdx, branch: null, branchIdx: null });
      (step.yes_branch || []).forEach((s, bi) => {
        out.push({ step: s, topIdx, branch: 'yes', branchIdx: bi });
      });
      (step.no_branch || []).forEach((s, bi) => {
        out.push({ step: s, topIdx, branch: 'no', branchIdx: bi });
      });
    });
    return out;
  }

  // Derive edges from sequential adjacency + condition branches.
  // Each connection becomes an SVG path string between node ports.
  private deriveEdges(): typeof this.canvasEdges {
    const edges: typeof this.canvasEdges = [];
    if (!this.design?.steps) return edges;
    const top = this.design.steps;

    for (let i = 0; i < top.length; i++) {
      const cur = top[i];
      const next = top[i + 1];
      // Linear adjacency at the top level — only when current isn't a
      // condition (conditions hand off to their branches, not a sibling)
      // AND current isn't a flow terminator.
      if (next && !this.isConditionStep(cur) && !this.isFlowTerminator(cur)) {
        edges.push(this.makeEdge(cur, next, 'main'));
      }
      // Branches: each arm is its own linear chain off the condition node.
      if (this.isConditionStep(cur)) {
        const yesArm = cur.yes_branch || [];
        if (yesArm[0]) edges.push(this.makeEdge(cur, yesArm[0], 'yes'));
        for (let j = 0; j < yesArm.length - 1; j++) {
          if (!this.isFlowTerminator(yesArm[j])) {
            edges.push(this.makeEdge(yesArm[j], yesArm[j + 1], 'main'));
          }
        }
        const noArm = cur.no_branch || [];
        if (noArm[0]) edges.push(this.makeEdge(cur, noArm[0], 'no'));
        for (let j = 0; j < noArm.length - 1; j++) {
          if (!this.isFlowTerminator(noArm[j])) {
            edges.push(this.makeEdge(noArm[j], noArm[j + 1], 'main'));
          }
        }
      }
    }
    return edges;
  }

  private makeEdge(
    from: DesignStep,
    to: DesignStep,
    kind: 'main' | 'yes' | 'no'
  ): { fromKey: string; toKey: string; kind: 'main' | 'yes' | 'no'; path: string } {
    return {
      fromKey: from.step_key,
      toKey: to.step_key,
      kind,
      path: this.computeEdgePath(from, to),
    };
  }

  // Cubic bezier from the bottom-center of the source node to the top-center
  // of the target node. Control points are pulled vertically so curves
  // naturally bend, even for offset nodes.
  private computeEdgePath(from: DesignStep, to: DesignStep): string {
    const x1 = (from.canvas_x || 0) + this.NODE_W / 2;
    const y1 = (from.canvas_y || 0) + this.NODE_H;
    const x2 = (to.canvas_x || 0) + this.NODE_W / 2;
    const y2 = to.canvas_y || 0;
    const dy = Math.max(40, (y2 - y1) / 2);
    return `M ${x1} ${y1} C ${x1} ${y1 + dy}, ${x2} ${y2 - dy}, ${x2} ${y2}`;
  }

  // Refresh just the edge paths after a drag — node positions changed but
  // structure didn't, so we don't need to rebuild the flat list.
  private refreshEdgePaths(): void {
    if (!this.design?.steps) return;
    const byKey: { [k: string]: DesignStep } = {};
    const visit = (s: DesignStep) => {
      byKey[s.step_key] = s;
      (s.yes_branch || []).forEach(visit);
      (s.no_branch || []).forEach(visit);
    };
    this.design.steps.forEach(visit);
    this.canvasEdges = this.canvasEdges.map((e) => ({
      ...e,
      path: this.computeEdgePath(byKey[e.fromKey], byKey[e.toKey]),
    }));
    // Branch anchors hang off the most-recent node in each arm — when that
    // node moves, recompute both anchor position and the dashed connector.
    this.canvasBranchAnchors = this.deriveBranchAnchors();
    this.canvasMainAnchor = this.deriveMainAnchor();
  }

  // Tail "+ Add" anchor for the top-level chain. Sits directly below the
  // last top-level step when that step is a non-terminator. If the chain
  // ends in a condition or End, the user adds via the YES/NO anchors
  // (or the chain is already explicitly terminated).
  private deriveMainAnchor(): {
    afterIdx: number;
    x: number;
    y: number;
    fromKey: string;
    edgePath: string;
  } | null {
    const steps = this.design?.steps;
    if (!steps || !steps.length) return null;
    const lastIdx = steps.length - 1;
    const last = steps[lastIdx];
    if (this.isFlowTerminator(last)) return null;
    const fromX = last.canvas_x || 0;
    const fromY = last.canvas_y || 0;
    const x = fromX;
    const y = fromY + this.LAYOUT_Y_SPACING;
    const cx = fromX + this.NODE_W / 2;
    const cy = fromY + this.NODE_H;
    const tx = x + this.NODE_W / 2;
    const ty = y;
    const dy = Math.max(40, (ty - cy) / 2);
    return {
      afterIdx: lastIdx,
      x,
      y,
      fromKey: last.step_key,
      edgePath: `M ${cx} ${cy} C ${cx} ${cy + dy}, ${tx} ${ty - dy}, ${tx} ${ty}`,
    };
  }

  // Compute "+ Add" anchor positions for every condition step, one per arm.
  // The anchor sits below the condition itself when the arm is empty, or
  // below the arm's last non-terminator step otherwise. Anchors that would
  // chain off an End / nested condition are suppressed since those steps
  // are flow terminators.
  private deriveBranchAnchors(): Array<{
    stepIdx: number;
    branch: 'yes' | 'no';
    afterIdx: number;
    x: number;
    y: number;
    fromKey: string;
    edgePath: string;
  }> {
    type Anchor = {
      stepIdx: number;
      branch: 'yes' | 'no';
      afterIdx: number;
      x: number;
      y: number;
      fromKey: string;
      edgePath: string;
    };
    const out: Anchor[] = [];
    if (!this.design?.steps) return out;
    const yOffset = this.LAYOUT_Y_SPACING;

    const buildAnchor = (
      stepIdx: number,
      branch: 'yes' | 'no',
      afterIdx: number,
      from: DesignStep,
      // Empty-arm anchors hang directly off the condition node and need a
      // sideways offset (matching auto-layout's branch column) so YES and
      // NO don't stack on top of each other and don't overlap a step that
      // already lives in the *other* arm. End-of-arm anchors don't need
      // this — they sit beneath an existing branch step that's already
      // been placed in the correct column.
      dx: number
    ) => {
      const fromX = from.canvas_x || 0;
      const fromY = from.canvas_y || 0;
      const x = fromX + dx;
      const y = fromY + yOffset;
      const cx = fromX + this.NODE_W / 2;
      const cy = fromY + this.NODE_H;
      const tx = x + this.NODE_W / 2;
      const ty = y;
      const horizontalReach = Math.abs(tx - cx);
      let edgePath: string;
      if (afterIdx === -1 && horizontalReach > 0) {
        // Empty-arm anchor: path travels mostly horizontally (~280px) with a
        // small vertical drop (~36px). The original vertical dy formula gives
        // dy=84 which exceeds ty-cy=36, causing the Bézier to loop backward.
        // Use horizontal control points instead — both C1 and C2 stay between
        // cy and ty so the curve flows cleanly without looping.
        const hspan = horizontalReach * 0.45;
        const dir = tx > cx ? 1 : -1;
        const vdrop = Math.max(2, (ty - cy) * 0.3);
        edgePath = `M ${cx} ${cy} C ${cx + dir * hspan} ${cy + vdrop}, ${tx - dir * hspan} ${ty - vdrop}, ${tx} ${ty}`;
      } else {
        // End-of-arm anchor (dx=0, horizontalReach≈0): standard vertical curve.
        const dy = Math.max(40, Math.max((ty - cy) / 2, horizontalReach * 0.3));
        edgePath = `M ${cx} ${cy} C ${cx} ${cy + dy}, ${tx} ${ty - dy}, ${tx} ${ty}`;
      }
      out.push({ stepIdx, branch, afterIdx, x, y, fromKey: from.step_key, edgePath });
    };

    this.design.steps.forEach((step, topIdx) => {
      if (!this.isConditionStep(step)) return;
      // Yes arm
      const yesArm = step.yes_branch || [];
      if (!yesArm.length) {
        buildAnchor(topIdx, 'yes', -1, step, this.LAYOUT_BRANCH_X);
      } else {
        const last = yesArm[yesArm.length - 1];
        if (!this.isFlowTerminator(last)) {
          buildAnchor(topIdx, 'yes', yesArm.length - 1, last, 0);
        }
      }
      // No arm
      const noArm = step.no_branch || [];
      if (!noArm.length) {
        buildAnchor(topIdx, 'no', -1, step, -this.LAYOUT_BRANCH_X);
      } else {
        const last = noArm[noArm.length - 1];
        if (!this.isFlowTerminator(last)) {
          buildAnchor(topIdx, 'no', noArm.length - 1, last, 0);
        }
      }
    });

    return out;
  }

  // Resize the inner canvas to fit the bounding box of all nodes plus a
  // generous margin, but never below a baseline so the dot-grid pattern
  // doesn't visually shrink for tiny flows.
  private recomputeCanvasBounds(): void {
    let maxX = 1200;
    let maxY = 600;
    this.canvasNodes.forEach((n) => {
      maxX = Math.max(maxX, (n.step.canvas_x || 0) + this.NODE_W + 200);
      maxY = Math.max(maxY, (n.step.canvas_y || 0) + this.NODE_H + 200);
    });
    // Anchors extend the bounding box so they don't get clipped on the
    // bottom-right edge of empty-condition arms.
    this.canvasBranchAnchors.forEach((a) => {
      maxX = Math.max(maxX, a.x + this.NODE_W + 200);
      maxY = Math.max(maxY, a.y + 80 + 200);
    });
    if (this.canvasMainAnchor) {
      maxX = Math.max(maxX, this.canvasMainAnchor.x + this.NODE_W + 200);
      maxY = Math.max(maxY, this.canvasMainAnchor.y + 80 + 200);
    }
    this.canvasW = maxX;
    this.canvasH = maxY;
  }

  // True while a step that was just added via the palette has not yet had
  // Apply clicked. Used by the template to show the unsaved-step banner and
  // by onNodeClick to block navigation away from the unapplied step.
  get isStepUnsaved(): boolean {
    return this.isNewStep && !this.hasBeenApplied;
  }

  // Click on a 2D node — route to either selectStep or selectBranchStep
  // based on the node's locator. Suppressed if a drag just ended.
  onNodeClick(node: { topIdx: number; branch: 'yes' | 'no' | null; branchIdx: number | null }): void {
    if (this.dragMoved) {
      this.dragMoved = false;
      return;
    }
    // Block navigation while an unapplied new step is open. The user must
    // Apply or Discard before selecting another node, otherwise the new
    // step would silently orphan in the design tree without being removed.
    if (this.isNewStep && !this.hasBeenApplied && this.configStepIdx !== null) {
      const isSameStep = node.branch
        ? this.configStepIdx === node.topIdx
            && this.configBranch === node.branch
            && this.configBranchIdx === node.branchIdx
        : this.configStepIdx === node.topIdx && this.configBranch === null;
      if (!isSameStep) {
        this.showSnack('Apply or Discard the new step before selecting another');
        return;
      }
    }
    if (node.branch && node.branchIdx !== null) {
      this.selectBranchStep(node.topIdx, node.branch, node.branchIdx);
    } else {
      this.selectStep(node.topIdx);
    }
  }

  // Begin a free-form drag on a node. We grab the current cursor position
  // relative to the canvas (un-zoomed) so during mousemove we can compute
  // the new node origin without accumulating drift.
  onNodeMouseDown(event: MouseEvent, step: DesignStep): void {
    // Only respond to left button without modifiers (Alt+drag still pans).
    if (event.button !== 0 || event.altKey) return;
    event.stopPropagation();
    this.dragNode = step;
    this.dragMoved = false;
    // Translate cursor → canvas coordinates accounting for current zoom.
    const canvas = this._canvasEl;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cursorX = (event.clientX - rect.left) / this.zoom;
    const cursorY = (event.clientY - rect.top) / this.zoom;
    this.dragOffsetX = cursorX - (step.canvas_x || 0);
    this.dragOffsetY = cursorY - (step.canvas_y || 0);
    document.body.style.cursor = 'grabbing';
  }

  @HostListener('document:mousemove', ['$event'])
  onDocMouseMoveDrag(event: MouseEvent): void {
    if (!this.dragNode) return;
    const canvas = this._canvasEl;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cursorX = (event.clientX - rect.left) / this.zoom;
    const cursorY = (event.clientY - rect.top) / this.zoom;
    let nx = cursorX - this.dragOffsetX;
    let ny = cursorY - this.dragOffsetY;
    // Snap to grid for tidy alignment.
    nx = Math.round(nx / this.LAYOUT_GRID) * this.LAYOUT_GRID;
    ny = Math.round(ny / this.LAYOUT_GRID) * this.LAYOUT_GRID;
    // Clamp so a node can't be dragged into negative space.
    nx = Math.max(0, nx);
    ny = Math.max(0, ny);
    if (nx !== this.dragNode.canvas_x || ny !== this.dragNode.canvas_y) {
      this.dragNode.canvas_x = nx;
      this.dragNode.canvas_y = ny;
      this.dragMoved = true;
      this.refreshEdgePaths();
      // Grow the canvas if user drags near the bounds.
      this.recomputeCanvasBounds();
      this.scheduleReapply();
    }
  }

  @HostListener('document:mouseup')
  onDocMouseUpDrag(): void {
    if (!this.dragNode) return;
    if (this.dragMoved) this.markDirty();
    this.dragNode = null;
    document.body.style.cursor = '';
  }

  // === FULLSCREEN ===
  // Real Fullscreen API — requestFullscreen on the canvas wrap. We can't
  // use CSS :fullscreen alone because the toolbar/minimap need to know the
  // state to render correct icons. Synced via the document event so Esc
  // works without manual handling.
  toggleFullscreen(): void {
    // Fullscreen the entire body (canvas + palette + config panel) so the
    // step library and config panel remain reachable after entering FS.
    const wrap = this.bodyWrapRef?.nativeElement || this.canvasWrapRef?.nativeElement;
    if (!wrap) return;
    const doc: any = document;
    const inFs = !!(doc.fullscreenElement || doc.webkitFullscreenElement);
    if (inFs) {
      const exit = doc.exitFullscreen || doc.webkitExitFullscreen;
      exit?.call(doc);
    } else {
      const req: any =
        (wrap as any).requestFullscreen ||
        (wrap as any).webkitRequestFullscreen;
      req?.call(wrap);
    }
  }

  @HostListener('document:fullscreenchange')
  @HostListener('document:webkitfullscreenchange')
  onFullscreenChange(): void {
    const doc: any = document;
    const fsEl = doc.fullscreenElement || doc.webkitFullscreenElement;
    const body = this.bodyWrapRef?.nativeElement;
    const wrap = this.canvasWrapRef?.nativeElement;
    // Match either ref so existing canvas-wrap fullscreen sessions (e.g.
    // mid-migration) still update isFullscreen correctly.
    this.isFullscreen = !!(fsEl && ((body && fsEl === body) || (wrap && fsEl === wrap)));
    // Viewport just changed dimensions dramatically — re-measure so the
    // canvas re-centers and the minimap reflects the new aspect.
    this.scheduleReapply();
  }


  private get currentUser(): string {
    return this.sessionService.getUsername() || "system";
  }

  // === LOAD ===

  loadDesigner(wfDefId: number): void {
    this.isLoadingDesigner = true;
    this.loadFailed = false;
    const sub = this.wfService.loadFull(wfDefId).subscribe({
      next: (response: any) => {
        if (response) {
          this.currentWf = response.definition;
          this.design = this.flattenDesign(response.design || this.emptyDesign());
          this.nextStepKey = this.computeNextStepKey(this.design);
          this.lastSavedName = this.currentWf?.wf_name || '';
          // Build the 2D canvas model — auto-layout fills missing positions
          // for legacy workflows; existing positions are preserved.
          this.rebuildCanvasModel();
          this.scrollToFirstNode();
        } else {
          // Load failed (handleError swallowed an HTTP error). Don't fabricate
          // a fresh currentWf from query-string data — saveFlow() would then
          // POST a blank design to the server's real DRAFT and erase it.
          this.currentWf = null;
          this.design = null;
          this.loadFailed = true;
          this.showSnack("Could not load this workflow. Reload to try again.");
        }
        this.isLoadingDesigner = false;
      },
      error: () => {
        this.currentWf = null;
        this.design = null;
        this.loadFailed = true;
        this.isLoadingDesigner = false;
      },
    });
    this.reqSubcription.push(sub);
  }

  private computeNextStepKey(design: DesignJSON): number {
    let max = 0;
    const visit = (s: DesignStep) => {
      const m = /(\d+)$/.exec(s.step_key || "");
      if (m) {
        const n = parseInt(m[1], 10);
        if (!isNaN(n) && n > max) max = n;
      }
      (s.yes_branch || []).forEach(visit);
      (s.no_branch || []).forEach(visit);
    };
    (design.steps || []).forEach(visit);
    return max + 1;
  }

  emptyDesign(): DesignJSON {
    return {
      version: 1,
      canvas: { scale: 1, pan_x: 60, pan_y: 60 },
      steps: [],
      connections: [],
    };
  }

  // === FLATTENING (handle .NET JsonElement wrappers) ===

  flatVal(v: any): any {
    if (v === null || v === undefined) return "";
    const t = typeof v;
    if (t === "string" || t === "number" || t === "boolean") return v;
    if (Array.isArray(v)) return v;
    if (v && typeof v === "object" && "ValueKind" in v) {
      switch (v.ValueKind) {
        case 0: // Undefined
        case 7: // Null
          return "";
        case 3: // String
          return v.Value ?? v.GetString ?? "";
        case 4: // Number
          return v.Value ?? v.GetDouble ?? v.GetInt32 ?? 0;
        case 5: // True
          return true;
        case 6: // False
          return false;
        default:
          return "";
      }
    }
    if (t === "object") {
      try {
        return JSON.stringify(v);
      } catch {
        return String(v);
      }
    }
    return String(v);
  }

  flattenConfig(cfg: any): { [key: string]: any } {
    if (!cfg || typeof cfg !== "object") return {};
    const out: { [key: string]: any } = {};
    for (const [k, v] of Object.entries(cfg)) {
      if (Array.isArray(v)) {
        out[k] = v.map((item) => this.flatVal(item));
      } else {
        out[k] = this.flatVal(v);
      }
    }
    return out;
  }

  flattenDesign(d: DesignJSON): DesignJSON {
    const visit = (step: DesignStep) => {
      step.config = this.flattenConfig(step.config);
      (step.yes_branch || []).forEach(visit);
      (step.no_branch || []).forEach(visit);
    };
    if (d && Array.isArray(d.steps)) {
      d.steps.forEach(visit);
    }
    return d;
  }

  // === PALETTE ===

  getFilteredPalette(): StepDefinition[] {
    const q = (this.paletteSearch || "").toLowerCase();
    return STEP_DEFS.filter(
      (s) =>
        s.type !== "trigger" &&
        s.id !== "act_loop" &&
        s.id !== "act_delay" &&
        (!q || s.name.toLowerCase().includes(q))
    );
  }

  getPaletteCategories(): string[] {
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const s of this.getFilteredPalette()) {
      if (!seen.has(s.cat)) {
        seen.add(s.cat);
        ordered.push(s.cat);
      }
    }
    return ordered;
  }

  getPaletteItemsByCategory(cat: string): StepDefinition[] {
    return this.getFilteredPalette().filter((s) => s.cat === cat);
  }

  // Branch-arm palette: same as the main palette but drops cond_if so a user
  // cannot start a branch with another condition (avoids unbounded nesting in
  // the data model and the UI).
  getBranchPalette(): StepDefinition[] {
    const q = (this.paletteSearch || "").toLowerCase();
    return STEP_DEFS.filter(
      (s) =>
        s.type !== "trigger" &&
        s.id !== "cond_if" &&
        s.id !== "act_loop" &&
        s.id !== "act_delay" &&
        (!q || s.name.toLowerCase().includes(q))
    );
  }

  getBranchPaletteCategories(): string[] {
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const s of this.getBranchPalette()) {
      if (!seen.has(s.cat)) {
        seen.add(s.cat);
        ordered.push(s.cat);
      }
    }
    return ordered;
  }

  getBranchPaletteItems(cat: string): StepDefinition[] {
    return this.getBranchPalette().filter((s) => s.cat === cat);
  }

  // === FLOW MANIPULATION ===

  // Build an empty step with default config wiring, including empty branch
  // arrays for cond_if so the rest of the rendering can rely on them.
  private buildStep(defId: string, def: StepDefinition, canvasY: number, stepOrder: number): DesignStep {
    const fields = FIELDS[def.fields] || [];
    const config: { [key: string]: any } = {};
    for (const f of fields) {
      if (!f.key) continue;
      if (f.default !== undefined) config[f.key] = f.default;
      if (f.type === "tags") config[f.key] = [];
    }
    const step: DesignStep = {
      step_key: "step_" + this.nextStepKey++,
      step_type_id: defId,
      step_label: def.name,
      canvas_x: 0,
      canvas_y: canvasY,
      step_order: stepOrder,
      config,
    };
    if (defId === "cond_if") {
      step.yes_branch = [];
      step.no_branch = [];
    }
    return step;
  }

  addStep(defId: string, afterIndex: number): void {
    const def = _getStepDef(defId);
    if (!def || !this.design) return;

    // Block if an unapplied new step is already open — covers the palette
    // sidebar path which calls addStep directly without going through any menu.
    if (this.isNewStep && !this.hasBeenApplied) {
      this.showSnack('Apply or Discard the current step before adding another');
      return;
    }

    // Conditions and End steps terminate their flow, so adding anything
    // after them at the top level is disallowed. The UI hides the "+" in
    // those positions; this is a defensive guard.
    const prev = this.design.steps[afterIndex];
    if (prev && this.isConditionStep(prev)) {
      this.showSnack("Add steps inside the Yes / No branch instead");
      this.closeAddMenu();
      return;
    }
    if (prev && this.isEndStep(prev)) {
      this.showSnack("Workflow already ends here");
      this.closeAddMenu();
      return;
    }

    const newStep = this.buildStep(defId, def, (afterIndex + 1) * 120, afterIndex + 2);
    // Position the new node directly below the previous step (or at the
    // canvas's default origin for the empty-state add). Without this the
    // node falls back to auto-layout's column/row defaults, which can
    // place it well above or off to the side of the click point.
    if (prev) {
      newStep.canvas_x = prev.canvas_x || this.LAYOUT_X_CENTER;
      newStep.canvas_y = (prev.canvas_y || this.LAYOUT_Y_START) + this.LAYOUT_Y_SPACING;
    } else {
      newStep.canvas_x = this.LAYOUT_X_CENTER;
      newStep.canvas_y = this.LAYOUT_Y_START;
    }
    this.design.steps.splice(afterIndex + 1, 0, newStep);
    this.rebuildCanvasModel();
    this.markDirty();
    this.selectStep(afterIndex + 1);
    this.isNewStep = true;
    this.hasBeenApplied = false;
    this.closeAddMenu();
  }

  // Add a step to the yes/no branch of a condition step at the given position
  // (afterIdx === -1 means insert at the start of the branch).
  addBranchStep(stepIdx: number, branch: 'yes' | 'no', afterIdx: number, defId: string): void {
    const def = _getStepDef(defId);
    if (!def || !this.design) return;

    // Block if an unapplied new step is already open. Without this guard,
    // clicking a +YES/+NO anchor while a new condition is unapplied would
    // let a branch step be added inside an unconfigured condition, causing
    // the canvas state to become inconsistent when either step is discarded.
    if (this.isNewStep && !this.hasBeenApplied) {
      this.showSnack('Apply or Discard the current step before adding another');
      return;
    }

    if (defId === 'cond_if') {
      // Defensive — the branch palette already filters this out.
      this.showSnack("Conditions can't start a branch");
      return;
    }
    const parent = this.design.steps[stepIdx];
    if (!parent || !this.isConditionStep(parent)) return;
    const arr = branch === 'yes' ? (parent.yes_branch ||= []) : (parent.no_branch ||= []);

    // Don't allow appending after an End step inside the branch.
    const prev = arr[afterIdx];
    if (prev && this.isEndStep(prev)) {
      this.showSnack("Branch already ends here");
      this.closeAddMenu();
      return;
    }

    const newStep = this.buildStep(defId, def, (afterIdx + 1) * 120, afterIdx + 2);
    // Position the new node where the "+ YES/NO" anchor lived — that's the
    // spot the user just clicked, so it's the least-surprising landing
    // point. The anchor was already computed (split-aware for empty-arm
    // condition nodes) so we reuse its coordinates verbatim.
    const anchor = this.canvasBranchAnchors.find(
      (a) => a.stepIdx === stepIdx && a.branch === branch && a.afterIdx === afterIdx
    );
    if (anchor) {
      newStep.canvas_x = anchor.x;
      newStep.canvas_y = anchor.y;
    } else if (prev) {
      newStep.canvas_x = prev.canvas_x || this.LAYOUT_X_CENTER;
      newStep.canvas_y = (prev.canvas_y || this.LAYOUT_Y_START) + this.LAYOUT_Y_SPACING;
    } else {
      const dx = branch === 'yes' ? this.LAYOUT_BRANCH_X : -this.LAYOUT_BRANCH_X;
      newStep.canvas_x = (parent.canvas_x || this.LAYOUT_X_CENTER) + dx;
      newStep.canvas_y = (parent.canvas_y || this.LAYOUT_Y_START) + this.LAYOUT_Y_SPACING;
    }
    arr.splice(afterIdx + 1, 0, newStep);
    this.rebuildCanvasModel();
    this.markDirty();
    this.selectBranchStep(stepIdx, branch, afterIdx + 1);
    this.isNewStep = true;
    this.hasBeenApplied = false;
    this.closeAddMenu();
  }

  removeStep(index: number): void {
    if (!this.design) return;
    this.design.steps.splice(index, 1);
    // If the removed step (or its parent) was the open config target, close.
    if (this.configStepIdx === index) {
      this.isNewStep = false;
      this.hasBeenApplied = false;
      this.closeConfig();
    } else if (this.configStepIdx !== null && this.configStepIdx > index) {
      this.configStepIdx--;
    }
    this.rebuildCanvasModel();
    this.markDirty();
  }

  removeBranchStep(stepIdx: number, branch: 'yes' | 'no', idx: number): void {
    if (!this.design) return;
    const parent = this.design.steps[stepIdx];
    if (!parent) return;
    const arr = branch === 'yes' ? parent.yes_branch : parent.no_branch;
    if (!arr) return;
    arr.splice(idx, 1);
    if (
      this.configStepIdx === stepIdx &&
      this.configBranch === branch &&
      this.configBranchIdx === idx
    ) {
      this.isNewStep = false;
      this.hasBeenApplied = false;
      this.closeConfig();
    } else if (
      this.configStepIdx === stepIdx &&
      this.configBranch === branch &&
      this.configBranchIdx !== null &&
      this.configBranchIdx > idx
    ) {
      this.configBranchIdx--;
    }
    this.rebuildCanvasModel();
    this.markDirty();
  }

  // === CONFIG PANEL ===

  selectStep(index: number): void {
    if (!this.design) return;
    this.configBranch = null;
    this.configBranchIdx = null;
    this.openConfigFor(this.design.steps[index] || null, index);
  }

  selectBranchStep(stepIdx: number, branch: 'yes' | 'no', branchIdx: number): void {
    if (!this.design) return;
    const parent = this.design.steps[stepIdx];
    if (!parent) return;
    const arr = branch === 'yes' ? parent.yes_branch : parent.no_branch;
    const step = arr?.[branchIdx];
    if (!step) return;
    this.configBranch = branch;
    this.configBranchIdx = branchIdx;
    this.openConfigFor(step, stepIdx);
  }

  // Resolves the step the config panel is currently editing — top-level when
  // configBranch is null, otherwise the branch step at the tracked index.
  getConfigStep(): DesignStep | null {
    if (this.configStepIdx === null || !this.design) return null;
    const top = this.design.steps[this.configStepIdx];
    if (!top) return null;
    if (this.configBranch && this.configBranchIdx !== null) {
      const arr = this.configBranch === 'yes' ? top.yes_branch : top.no_branch;
      return arr?.[this.configBranchIdx] || null;
    }
    return top;
  }

  private openConfigFor(step: DesignStep | null, topLevelIdx: number): void {
    // Selecting a step defaults to "existing step" semantics. add* methods
    // flip isNewStep back to true after calling select*.
    this.isNewStep = false;
    this.hasBeenApplied = false;
    this.configStepIdx = topLevelIdx;
    // Snapshot palette state then collapse it so the config panel gets the
    // full right column. Restored in closeConfig() so Apply/Discard/Escape
    // all return the palette to whatever the user had it set to before.
    // Clear the search term so the palette always reopens showing the full
    // step list rather than a stale filter that may match nothing.
    this.paletteSearch = '';
    this.paletteStateBeforeConfig = this.paletteOpen;
    this.paletteOpen = false;
    if (!step) return;
    const def = _getStepDef(step.step_type_id);
    if (!def) return;

    const fields = FIELDS[def.fields] || [];
    const controls: { [k: string]: any } = {
      step_label: [step.step_label, Validators.required],
    };
    this.tagValues = {};
    this.assigneeInput = '';
    this.showAssigneeResults = false;
    this.assigneeFieldKey = null;
    // Reset status-picker transient state so stale results / open dropdown
    // from a previous step config don't bleed into this one.
    this.statusPickerInput = '';
    this.statusPickerResults = [];
    this.showStatusResults = false;
    this.statusPickerFieldKey = null;

    for (const f of fields) {
      if (f.section || !f.key) continue;
      if (f.type === "tags" || f.type === "assignees" || f.type === "status-picker") {
        // All three store a list of strings; the UI input method differs.
        this.tagValues[f.key] = Array.isArray(step.config[f.key])
          ? [...step.config[f.key]]
          : [];
      } else if (f.type === "toggle") {
        const v = step.config[f.key];
        controls[f.key] = [v === true || v === "true"];
      } else {
        const existing = step.config[f.key];
        const initial =
          existing !== undefined
            ? existing
            : f.default !== undefined
            ? f.default
            : "";
        controls[f.key] = [initial, f.req ? Validators.required : null];
      }
    }

    this.configForm = this.fb.group(controls);
    this.currentFields = fields;
    this.rebuildOptionCache();

    const userAutoFillKeys = ["owner_id", "updated_by", "assignee_email"];
    userAutoFillKeys.forEach((key) => {
      const control = this.configForm?.get(key);
      if (control && (!control.value || control.value === "")) {
        control.setValue(this.currentUserEmail);
      }
    });

    // If this step has any 'assignees' field with saved emails, eager-load the
    // user list so the existing chips render with real display names instead
    // of bare email strings on first paint.
    const hasSavedAssignees = fields.some(
      (f) => f.type === "assignees" && f.key && (this.tagValues[f.key]?.length || 0) > 0
    );
    if (hasSavedAssignees && !this.assigneeUsersCache.length) {
      this.loadAllUsers();
    }

    // Status-picker: pre-warm the code list whenever a step that uses this
    // field type is opened. This eliminates two UX issues:
    //   1. First focus delay — the dropdown now opens instantly with results
    //      rather than waiting for an API round-trip after the user clicks.
    //   2. Chips showing raw codes — if the cache is already warm, rebuild
    //      statusDisplayMap immediately so existing saved values render with
    //      friendly descriptions (patient_reason_code_desc) rather than the
    //      raw reason code (e.g. "APS05") while the async load is in flight.
    const hasStatusPickerField = fields.some((f) => f.type === 'status-picker');
    if (hasStatusPickerField) {
      if (this.statusPickerCache.length) {
        for (const s of this.statusPickerCache) {
          this.statusDisplayMap[s.code] = s.label || s.code;
        }
      } else {
        this.loadStatusCodes();
      }
    }

    // Email Alert step has a "Choose template" toggle that mutually excludes
    // Subject/Body and email_template_key. Sync validators to the saved value
    // immediately, then subscribe so user toggles flip them in real time.
    if (def.fields === 'email_alert') {
      this.wireEmailAlertToggle();
    }
  }

  /**
   * Sync use_template ↔ subject/body/email_template_key validators and value
   * clearing. Initial pass fires for the saved value; the subscription keeps
   * the form in sync as the user flips the toggle. Disposed in closeConfig().
   */
  private wireEmailAlertToggle(): void {
    if (!this.configForm) return;
    const toggle = this.configForm.get('use_template');
    if (!toggle) return;
    this.applyEmailAlertToggle(!!toggle.value);
    this.configToggleSub = toggle.valueChanges.subscribe((v: any) => {
      this.applyEmailAlertToggle(!!v);
    });
  }

  private applyEmailAlertToggle(useTemplate: boolean): void {
    if (!this.configForm) return;
    const subject = this.configForm.get('subject');
    const body = this.configForm.get('body');
    const tmpl = this.configForm.get('email_template_key');
    if (useTemplate) {
      // Template-mode: clear the inline message inputs so a stale subject/body
      // can't accidentally save alongside the template selection.
      subject?.setValue('', { emitEvent: false });
      body?.setValue('', { emitEvent: false });
      subject?.clearValidators();
      body?.clearValidators();
      tmpl?.setValidators([Validators.required]);
    } else {
      // Manual-mode: drop the template selection and restore message validators
      // so the user has to fill Subject/Body before Apply succeeds.
      tmpl?.setValue(null, { emitEvent: false });
      tmpl?.clearValidators();
      subject?.setValidators([Validators.required]);
      body?.setValidators([Validators.required]);
    }
    subject?.updateValueAndValidity({ emitEvent: false });
    body?.updateValueAndValidity({ emitEvent: false });
    tmpl?.updateValueAndValidity({ emitEvent: false });
  }

  // Stable accessor for the template's outer field loop. Returns the same
   // array reference between change-detection ticks until selectStep() rebuilds
   // it for a new step.
  getFieldsForStep(): NewFieldSpec[] {
    return this.currentFields;
  }

  /**
   * Mirrors the showWhen / hideWhen guards used in the template so applyConfig
   * can skip required-field validation for inputs the user can't see.
   */
  private isFieldVisible(field: NewFieldSpec): boolean {
    if (!this.configForm) return true;
    if (field.showWhen) {
      const v = this.configForm.get(field.showWhen.key)?.value;
      if (v !== field.showWhen.equals) return false;
    }
    if (field.hideWhen) {
      const v = this.configForm.get(field.hideWhen.key)?.value;
      if (v === field.hideWhen.equals) return false;
    }
    return true;
  }

  private rebuildOptionCache(): void {
    if (!this.currentFields.length) {
      this.optionCache = {};
      return;
    }
    const next: { [key: string]: { id: any; name: string }[] } = {};
    for (const f of this.currentFields) {
      if (!f.key || f.section || f.type !== "select") continue;
      next[f.key] = this.computeOptsForField(f);
    }
    this.optionCache = next;
  }

  // Public passthrough kept for resolveLookup() (called by canvas card
   // previews). The config panel template binds to optionCache directly.
  getOptsForField(field: NewFieldSpec): { id: any; name: string }[] {
    if (field.key && this.optionCache[field.key]) return this.optionCache[field.key];
    return this.computeOptsForField(field);
  }

  private computeOptsForField(field: NewFieldSpec): { id: any; name: string }[] {
    if (field.opts_api) {
      const src: any[] = (this.lookups as any)[field.opts_api];
      if (Array.isArray(src)) {
        // WorkflowService returns FieldOption[] = { label, value }; fall back
        // to raw API shape { id, name } if future callers bypass the mapper.
        return src.map((x: any) => ({
          id: x?.value ?? x?.id ?? x?.task_status_id ?? x?.task_priority_id ?? x?.task_type_id ?? null,
          name: x?.label ?? x?.name ?? x?.status_name ?? x?.priority_name ?? x?.type_name ?? "",
        }));
      }
      return [];
    }
    if (field.opts) {
      return field.opts.map((o) => ({ id: o, name: o }));
    }
    return [];
  }

  // mat-select compares values with strict equality by default; the form-control
  // side may carry a numeric ID (from the API) while the option side may be the
  // same value as a string (after a JSON round-trip). Coerce both to strings so
  // a saved task_status_id survives the trip back into the dropdown.
  compareById = (a: any, b: any): boolean => {
    if (a === null || a === undefined || a === "") return b === null || b === undefined || b === "";
    if (b === null || b === undefined || b === "") return false;
    return String(a) === String(b);
  };

  applyConfig(): void {
    if (!this.design || !this.configForm) return;
    const step = this.getConfigStep();
    if (!step) return;

    // Validate FormGroup-backed fields (text, number, select, textarea, date, etc.)
    // and required tag/assignee fields (stored in tagValues, outside the form).
    if (this.configForm.invalid) {
      this.configForm.markAllAsTouched();
      this.showSnack("Please fill all required fields");
      return;
    }
    const missingListField = this.currentFields.find(
      (f) =>
        f.req &&
        f.key &&
        (f.type === "tags" || f.type === "assignees" || f.type === "status-picker") &&
        this.isFieldVisible(f) &&
        !(this.tagValues[f.key] && this.tagValues[f.key].length)
    );
    if (missingListField) {
      this.showSnack(`Please add at least one ${missingListField.label}`);
      return;
    }

    const formVals = { ...this.configForm.value };
    step.step_label = formVals.step_label;
    delete formVals.step_label;

    for (const k of Object.keys(formVals)) {
      step.config[k] = formVals[k];
    }
    for (const k of Object.keys(this.tagValues)) {
      step.config[k] = [...this.tagValues[k]];
    }

    this.markDirty();
    this.hasBeenApplied = true;
    this.showSnack("Applied — click Save to persist");
    this.closeConfig();
  }

  closeConfig(): void {
    // If the user added a step via the palette and never Applied, drop it
    // from the canvas on close/discard so orphan default-config steps don't
    // accumulate. Splice from the right list (top-level vs branch).
    if (
      this.isNewStep &&
      !this.hasBeenApplied &&
      this.configStepIdx !== null &&
      this.design
    ) {
      if (this.configBranch && this.configBranchIdx !== null) {
        const parent = this.design.steps[this.configStepIdx];
        const arr = parent
          ? this.configBranch === 'yes'
            ? parent.yes_branch
            : parent.no_branch
          : null;
        if (arr) arr.splice(this.configBranchIdx, 1);
      } else {
        this.design.steps.splice(this.configStepIdx, 1);
      }
    }
    this.configToggleSub?.unsubscribe();
    this.configToggleSub = null;
    this.configStepIdx = null;
    this.configBranch = null;
    this.configBranchIdx = null;
    this.configForm = null;
    this.tagValues = {};
    this.currentFields = [];
    this.optionCache = {};
    this.assigneeInput = '';
    this.assigneeSearchResults = [];
    this.showAssigneeResults = false;
    this.assigneeFieldKey = null;
    this.statusPickerInput = '';
    this.statusPickerResults = [];
    this.showStatusResults = false;
    this.statusPickerFieldKey = null;
    this.isNewStep = false;
    this.hasBeenApplied = false;
    // Restore the palette to the state it was in before the config opened.
    // Covers Apply, Discard, × button, and Escape uniformly.
    this.paletteOpen = this.paletteStateBeforeConfig;
    // Always rebuild the canvas after any config close so nodes/edges/anchors
    // are guaranteed to reflect the current design state. Running this once
    // unconditionally at the end (after all state resets) is safer than the
    // previous in-block call, which could be skipped by edge-case flag states.
    if (this.design) this.rebuildCanvasModel();
  }

  // === TAGS ===

  addTag(fieldKey: string, value: string): void {
    if (!value || !value.trim()) return;
    if (!this.tagValues[fieldKey]) this.tagValues[fieldKey] = [];
    const trimmed = value.trim();
    if (!this.tagValues[fieldKey].includes(trimmed)) {
      this.tagValues[fieldKey].push(trimmed);
    }
  }

  removeTag(fieldKey: string, index: number): void {
    if (this.tagValues[fieldKey]) {
      this.tagValues[fieldKey].splice(index, 1);
    }
  }

  // === ASSIGNEE SEARCHABLE PICKER ===
  // Mirrors task-detail-content's pattern: lazy-load full user list once via
  // TaskMgmtService.getAllUserForImpersonate(), then client-side filter on
  // each keystroke (200ms debounce).

  onAssigneeSearchFocus(fieldKey: string): void {
    this.assigneeFieldKey = fieldKey;
    this.showAssigneeResults = true;
    if (!this.assigneeUsersCache.length) {
      this.loadAllUsers();
    } else {
      this.filterUsers(this.assigneeInput);
    }
  }

  onAssigneeSearchInput(event: Event): void {
    const value = ((event.target as HTMLInputElement).value || '').trim();
    this.showAssigneeResults = true;
    if (this.assigneeSearchTimer) clearTimeout(this.assigneeSearchTimer);
    this.assigneeSearchTimer = setTimeout(() => this.filterUsers(value), 200);
  }

  onAssigneeSearchBlur(): void {
    // Defer so the result row's mousedown can register the selection first.
    if (this.assigneeBlurTimer) clearTimeout(this.assigneeBlurTimer);
    this.assigneeBlurTimer = setTimeout(() => {
      this.showAssigneeResults = false;
    }, 200);
  }

  private loadAllUsers(): void {
    this.isSearchingUsers = true;
    const sub = this.taskMgmtService.getAllUserForImpersonate('').subscribe({
      next: (response: any) => {
        this.assigneeUsersCache = Array.isArray(response)
          ? response
          : (response?.data || []);
        // Pre-populate display map from current selection so saved emails get
        // friendly names without waiting for the user to interact.
        this.refreshAssigneeDisplayMap();
        this.filterUsers(this.assigneeInput);
        this.isSearchingUsers = false;
      },
      error: () => {
        this.assigneeUsersCache = [];
        this.assigneeSearchResults = [];
        this.isSearchingUsers = false;
      },
    });
    this.reqSubcription.push(sub);
  }

  private filterUsers(keyword: string): void {
    if (!this.assigneeUsersCache) return;
    if (!keyword) {
      this.assigneeSearchResults = this.assigneeUsersCache.slice(0, 50);
      return;
    }
    const lower = keyword.toLowerCase();
    this.assigneeSearchResults = this.assigneeUsersCache
      .filter(
        (u: any) =>
          (u.usr_display_nm || '').toLowerCase().includes(lower) ||
          (u.user_id || '').toLowerCase().includes(lower)
      )
      .slice(0, 50);
  }

  private refreshAssigneeDisplayMap(): void {
    for (const u of this.assigneeUsersCache) {
      if (u?.user_id) {
        this.assigneeDisplayMap[u.user_id] = u.usr_display_nm || u.user_id;
      }
    }
  }

  selectAssignee(fieldKey: string, user: any): void {
    const email = user?.user_id;
    if (!email) return;
    if (!this.tagValues[fieldKey]) this.tagValues[fieldKey] = [];
    if (this.tagValues[fieldKey].includes(email)) return;
    this.tagValues[fieldKey].push(email);
    this.assigneeDisplayMap[email] = user?.usr_display_nm || email;
    this.assigneeInput = '';
    this.markDirty();
  }

  removeAssignee(fieldKey: string, index: number): void {
    if (this.tagValues[fieldKey]) {
      this.tagValues[fieldKey].splice(index, 1);
      this.markDirty();
    }
  }

  getAssignees(fieldKey: string): string[] {
    return this.tagValues[fieldKey] || [];
  }

  isAssigneeSelected(fieldKey: string, user: any): boolean {
    const email = user?.user_id;
    if (!email) return false;
    return (this.tagValues[fieldKey] || []).includes(email);
  }

  // === PATIENT STATUS PICKER ===

  onStatusPickerFocus(fieldKey: string): void {
    this.statusPickerFieldKey = fieldKey;
    this.showStatusResults = true;
    if (!this.statusPickerCache.length) {
      this.loadStatusCodes();
    } else {
      this.filterStatusCodes(this.statusPickerInput);
    }
  }

  onStatusPickerInput(event: Event): void {
    const value = ((event.target as HTMLInputElement).value || '').trim();
    this.showStatusResults = true;
    if (this.statusPickerTimer) clearTimeout(this.statusPickerTimer);
    this.statusPickerTimer = setTimeout(() => this.filterStatusCodes(value), 200);
  }

  onStatusPickerBlur(): void {
    if (this.statusPickerBlurTimer) clearTimeout(this.statusPickerBlurTimer);
    this.statusPickerBlurTimer = setTimeout(() => {
      this.showStatusResults = false;
    }, 200);
  }

  private loadStatusCodes(): void {
    if (this.isLoadingStatuses) return;
    this.isLoadingStatuses = true;
    // const cliKey = Number(this.clientKey) || 4001;
    // const brandKey = this.brandKey != null ? String(this.brandKey) : '400101';
    const sub = this.wfService.GetpatientStatusCodeFilter(4001, '400101').subscribe({
      next: (res: any) => {
        const items: any[] = Array.isArray(res) ? res
          : Array.isArray(res?.data) ? res.data
          : Array.isArray(res?.items) ? res.items
          : Array.isArray(res?.result) ? res.result
          : [];
        this.statusPickerCache = items.map((item: any) => ({
          code: String(item?.patient_status_reason_code ?? item?.code ?? item?.status_code ?? item?.id ?? ''),
          label: String(item?.patient_reason_code_desc ?? item?.description ?? item?.label ?? item?.name ?? ''),
        })).filter((s) => s.code);
        for (const s of this.statusPickerCache) {
          this.statusDisplayMap[s.code] = s.label || s.code;
        }
        this.filterStatusCodes(this.statusPickerInput);
        this.isLoadingStatuses = false;
      },
      error: () => {
        this.statusPickerCache = [];
        this.statusPickerResults = [];
        this.isLoadingStatuses = false;
      },
    });
    this.reqSubcription.push(sub);
  }

  private filterStatusCodes(keyword: string): void {
    if (!keyword) {
      this.statusPickerResults = this.statusPickerCache.slice(0, 50);
      return;
    }
    const lower = keyword.toLowerCase();
    this.statusPickerResults = this.statusPickerCache
      .filter((s) =>
        s.label.toLowerCase().includes(lower) ||
        s.code.toLowerCase().includes(lower)
      )
      .slice(0, 50);
  }

  selectStatus(fieldKey: string, item: { code: string; label: string }): void {
    if (!item?.code) return;
    if (!this.tagValues[fieldKey]) this.tagValues[fieldKey] = [];
    if (this.tagValues[fieldKey].includes(item.code)) return;
    this.tagValues[fieldKey].push(item.code);
    this.statusDisplayMap[item.code] = item.label || item.code;
    this.statusPickerInput = '';
    this.markDirty();
  }

  removeStatus(fieldKey: string, index: number): void {
    if (this.tagValues[fieldKey]) {
      this.tagValues[fieldKey].splice(index, 1);
      this.markDirty();
    }
  }

  getStatusValues(fieldKey: string): string[] {
    return this.tagValues[fieldKey] || [];
  }

  isStatusSelected(fieldKey: string, item: { code: string; label: string }): boolean {
    if (!item?.code) return false;
    return (this.tagValues[fieldKey] || []).includes(item.code);
  }

  // === SAVE / LIFECYCLE ===

  saveFlow(): void {
    if (!this.currentWf || !this.design) return;
    const payload = {
      wf_name: this.currentWf.wf_name,
      description: this.currentWf.description || "",
      updated_by: this.currentUser,
      design: this.design,
    };
    const sub = this.wfService
      .saveFull(this.currentWf.wf_def_id, payload)
      .subscribe((res: any) => {
        if (res) {
          this.currentWf = res.definition || this.currentWf;
          this.design = res.design ? this.flattenDesign(res.design) : this.design;
          this.lastSavedName = this.currentWf?.wf_name || '';
          this.markClean();
          this.showSnack("Saved");
        } else {
          this.showSnack("Failed to save workflow");
        }
      });
    this.reqSubcription.push(sub);
  }

  activateFlow(): void {
    if (!this.design?.steps?.length) {
      this.showSnack("Add steps first");
      return;
    }
    if (!this.currentWf) return;
    const payload = {
      wf_name: this.currentWf.wf_name,
      description: this.currentWf.description || "",
      updated_by: this.currentUser,
      design: this.design,
    };
    const sub = this.wfService
      .saveFull(this.currentWf.wf_def_id, payload)
      .subscribe((saved: any) => {
        if (!saved) {
          this.showSnack("Save failed — cannot activate");
          return;
        }
        if (saved.definition) this.currentWf = saved.definition;
        if (saved.design) this.design = this.flattenDesign(saved.design);
        this.lastSavedName = this.currentWf?.wf_name || '';
        this.markClean();

        if (!this.currentWf) return;
        const actSub = this.wfService
          .activateDefinition(this.currentWf.wf_def_id, this.currentUser)
          .subscribe((res: any) => {
            if (res && this.currentWf) {
              this.currentWf.status = "ACTIVE";
              this.showSnack("Activated");
            } else {
              this.showSnack("Failed to activate workflow");
            }
          });
        this.reqSubcription.push(actSub);
      });
    this.reqSubcription.push(sub);
  }

  pauseFlow(): void {
    if (!this.currentWf) return;
    const sub = this.wfService
      .pauseDefinition(this.currentWf.wf_def_id)
      .subscribe((res: any) => {
        if (res && this.currentWf) {
          this.currentWf.status = "PAUSED";
          this.showSnack("Paused");
        } else {
          this.showSnack("Failed to pause workflow");
        }
      });
    this.reqSubcription.push(sub);
  }

  updateWorkflowMeta(): void {
    if (!this.currentWf) return;
    const nameAtRequest = this.currentWf.wf_name;
    const sub = this.wfService.updateDefinition({
      wf_def_id: this.currentWf.wf_def_id,
      wf_name: nameAtRequest,
      description: this.currentWf.description || '',
      updated_by: this.currentUser,
    }).subscribe((res: any) => {
      if (res) {
        // Only commit lastSavedName when the server confirms — otherwise the
        // blur stays "dirty" and a future blur retries (instead of silently
        // assuming success and never trying again).
        this.lastSavedName = nameAtRequest;
      }
    });
    this.reqSubcription.push(sub);
  }

  onNameBlur(): void {
    // Compare against the last persisted name, not the global `dirty` flag —
    // dirty is also flipped by step edits, which would otherwise race
    // updateWorkflowMeta against an in-flight saveFlow on unrelated changes.
    if (!this.currentWf) return;
    const current = (this.currentWf.wf_name || '').trim();
    if (!current) return;
    if (current === this.lastSavedName) return;
    this.updateWorkflowMeta();
  }

  // === DIRTY TRACKING ===

  markDirty(): void {
    this.dirty = true;
  }

  markClean(): void {
    this.dirty = false;
  }

  // === ADD MENU ===

  toggleAddMenu(index: number): void {
    if (this.isNewStep && !this.hasBeenApplied) {
      this.showSnack('Apply or Discard the current step before adding another');
      return;
    }
    this.activeAddMenu = this.activeAddMenu === index ? null : index;
    this.activeBranchAddMenu = null;
  }

  toggleBranchAddMenu(stepIdx: number, branch: 'yes' | 'no', afterIdx: number): void {
    if (this.isNewStep && !this.hasBeenApplied) {
      this.showSnack('Apply or Discard the current step before adding another');
      return;
    }
    const cur = this.activeBranchAddMenu;
    const same =
      cur && cur.stepIdx === stepIdx && cur.branch === branch && cur.afterIdx === afterIdx;
    this.activeBranchAddMenu = same ? null : { stepIdx, branch, afterIdx };
    this.activeAddMenu = null;
  }

  isBranchAddMenuOpen(stepIdx: number, branch: 'yes' | 'no', afterIdx: number): boolean {
    const c = this.activeBranchAddMenu;
    return !!c && c.stepIdx === stepIdx && c.branch === branch && c.afterIdx === afterIdx;
  }

  closeAddMenu(): void {
    this.activeAddMenu = null;
    this.activeBranchAddMenu = null;
  }

  // === DISPLAY HELPERS ===

  getConfigPreviewRows(step: DesignStep): { label: string; value: string }[] {
    const def = _getStepDef(step.step_type_id);
    if (!def) return [];
    const fields = FIELDS[def.fields] || [];
    return fields
      .filter(
        (f) =>
          !!f.key &&
          !f.section &&
          !f.hidden &&
          step.config[f.key!] !== undefined &&
          step.config[f.key!] !== ""
      )
      .slice(0, 3)
      .map((f) => {
        let v: any = this.flatVal(step.config[f.key!]);
        if (Array.isArray(v)) {
          if (f.type === 'status-picker') {
            v = v.map((code: string) => this.statusDisplayMap[code] || code).join(', ');
          } else {
            v = v.join(', ');
          }
        } else if (v === true) {
          v = "Yes";
        } else if (v === false) {
          v = "No";
        } else {
          v = this.resolveLookup(f, v);
        }
        return { label: f.label, value: String(v) };
      });
  }

  resolveLookup(field: NewFieldSpec, value: any): string {
    if (field.opts_api) {
      const match = this.getOptsForField(field).find(
        (x) => String(x.id) === String(value)
      );
      if (match) return match.name;
    }
    return String(value === null || value === undefined ? "" : value);
  }

  getStepDef(id: string): StepDefinition | undefined {
    return _getStepDef(id);
  }

  isConditionStep(step: DesignStep): boolean {
    const def = _getStepDef(step.step_type_id);
    return def?.type === "condition";
  }

  isEndStep(step: DesignStep): boolean {
    const def = _getStepDef(step.step_type_id);
    return def?.type === "end";
  }

  // True for any step that terminates its containing flow — the "+" should
  // not be drawn after such a step. Conditions terminate via branches; End
  // terminates outright.
  isFlowTerminator(step: DesignStep): boolean {
    return this.isConditionStep(step) || this.isEndStep(step);
  }

  // Show the per-arm "End" marker only when the branch has at least one step
  // and its last step isn't already an End/terminator (avoids stacking two
  // end indicators).
  shouldShowBranchEnd(step: DesignStep, branch: 'yes' | 'no'): boolean {
    const arr = branch === 'yes' ? step.yes_branch : step.no_branch;
    if (!arr || !arr.length) return false;
    return !this.isFlowTerminator(arr[arr.length - 1]);
  }

  isTriggerStep(step: DesignStep): boolean {
    return !!step.step_type_id && step.step_type_id.startsWith("trigger_");
  }

  getTriggerClass(): string {
    if (!this.design?.steps?.length) return "manual";
    const t = this.design.steps[0]?.step_type_id;
    if (t === "trigger_event") return "event";
    if (t === "trigger_schedule") return "schedule";
    return "manual";
  }

  getBranchLabels(step: DesignStep): { yes: string; no: string } {
    return {
      yes: step.config?.["true_label"] || "Yes",
      no: step.config?.["false_label"] || "No",
    };
  }

  // True when ANY part of the right sidebar is visible (palette or config panel).
  // Used by the toggle button so its arrow accurately reflects the sidebar state.
  get effectivePaletteOpen(): boolean {
    return this.configStepIdx !== null || this.paletteOpen;
  }

  togglePalette(): void {
    if (this.configStepIdx !== null) {
      // Config panel is open — collapse the entire sidebar.
      // Guard: never silently discard unsaved step config changes.
      if (this.isStepUnsaved) return;
      // Pre-set restore state to false so closeConfig() leaves the sidebar hidden.
      this.paletteStateBeforeConfig = false;
      this.closeConfig();
      return;
    }
    this.paletteOpen = !this.paletteOpen;
  }

  // === KEYBOARD ===

  @HostListener("document:keydown", ["$event"])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      this.closeConfig();
      this.closeAddMenu();
    }
    if (event.ctrlKey && event.key === "s" && this.currentWf) {
      event.preventDefault();
      this.saveFlow();
    }
    // Ctrl/Cmd + (+|-|0) → zoom controls. We swallow so the browser doesn't
    // additionally zoom the page chrome. "=" is the unmodified key for "+".
    if ((event.ctrlKey || event.metaKey) && (event.key === "+" || event.key === "=")) {
      event.preventDefault();
      this.zoomIn();
    } else if ((event.ctrlKey || event.metaKey) && event.key === "-") {
      event.preventDefault();
      this.zoomOut();
    } else if ((event.ctrlKey || event.metaKey) && event.key === "0") {
      event.preventDefault();
      this.zoomReset();
    }
  }

  // === HELPERS ===

  private showSnack(message: string): void {
    this.snackBar.open(message, "Close", { duration: 3000 });
  }
}
